import { CooperationViewService } from "./CooperationViewService";
import { CooperationPersistenceService } from "./CooperationPersistenceService";
import { CooperationApiService } from "./CooperationApiService";
import { CooperationDataModel } from "../models/view/CooperationData";

export class CooperationService<T extends MglAppDefinition> {
	private cooperationViewService: CooperationViewService;
	private readonly cooperationApiService: CooperationApiService<MglAppDefinition>;
	private readonly cooperationPersistenceService: CooperationPersistenceService;
	private readonly options: BasicAppOptions<T>;
	private readonly $ctx: JQuery;

	constructor(options: BasicAppOptions<T>) {
		this.options = options;
		this.$ctx = $(this.options.$ctx);
		this.cooperationPersistenceService = new CooperationPersistenceService();
		this.cooperationApiService = new CooperationApiService(this.options);
	}

	/***
	 * Must be called after the DOM is built. e.g. in an applications afterRender method.
	 */
	public initCooperationViewService(): void {
		this.cooperationViewService = new CooperationViewService(this.$ctx.find('.js-cooperation'));
	}

	/***
	 * merges the active validatorRules with the validatorRules defined in the markup.
	 * @param validatorRules active validatorRules
	 */
	public mergeValidatorRules(validatorRules: ValidationRules): ValidationRules {
		this.checkCooperationViewService();
		return this.cooperationViewService.mergeWithValidatorRulesFromMarkup(validatorRules);
	}

	/***
	 * Checks whether the view service has been initialized, else outputs a hint for the developer.
	 * @throws Exception, if the CooperationViewService is
	 */
	private checkCooperationViewService(): void {
		if (!this.cooperationViewService) {
			throw new ReferenceError("cooperationViewService is not defined. Did you forget to run initCooperationViewService?");
		}
	}

	/***
	 * Stores the CooperationModel in sessionStorage.
	 */
	public setModelToStore(processId: number): void {
		this.checkCooperationViewService();
		const baseModel = this.cooperationPersistenceService.getCooperationModelFromStore() || new CooperationDataModel();
		baseModel.Dynamics = this.cooperationViewService.getDynamicsModelFromMarkup();
		baseModel.Siteurl = window.location.href;
		baseModel.Processid = processId as unknown as string;
		baseModel.Formid = this.options.app.formid as unknown as string;
		const cooperationFormModel = this.cooperationViewService.getCooperationFormModelFromMarkup();
		const cooperationModel = $.extend(true, {}, baseModel, cooperationFormModel);
		this.cooperationPersistenceService.setCooperationModelToStore(cooperationModel);
	}

	/***
	 * Updates the values in the view.
	 */
	public setModelToView(model: { [key: string]: any }): void {
		this.checkCooperationViewService();
		this.cooperationViewService.setModelToView(model);
	}

	/***
	 * Gets the CooperationModel from the sessionStorage.
	 */
	/* istanbul ignore next: wrapper function */
	public getModelFromStore(): CooperationDataModel {
		return this.cooperationPersistenceService.getCooperationModelFromStore();
	}

	/***
	 * Gets the DynamicFields component of the CooperationModel from the sessionStorage.
	 */
	/* istanbul ignore next: wrapper function */
	public getDynamicFieldsFromStore(): { [key: string]: any } {
		return this.cooperationPersistenceService.getDynamicsFieldsFromStore();
	}

	/***
	 * Submits the cooperation data to the cooperation api.
	 */
	public submitCooperationData(successCallback: () => void,
		errorCallback: (jqxhr?: JQuery.jqXHR) => void,
		personalData: { [key: string]: any }): void {
		let model = this.cooperationPersistenceService.getCooperationModelFromStore();
		model = $.extend(true, {}, model, personalData);
		this.cooperationApiService.submitCooperationData(model, successCallback, errorCallback);
	}

	/***
	 * Clears the cooperation-specific content in session storage.
	 */
	/* istanbul ignore next: wrapper function */
	public clearSession(): void {
		this.cooperationPersistenceService.clearSession();
	}
}
